import React, { useCallback, useEffect, useState } from 'react';
import { engagementApi, leadApi, openAIAdsApi } from '../services/api';
import { BySource, ChatGPTCampaign, ChatGPTReport, LeadRow, SourceFunnel } from '../types';
import { fmtDayTime, fmtINR, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import StageChips, { Dash } from '../components/StageChips';
import {
  Section, Empty, Failed, Loading, Note, Out, PageFrame, picked, Pill, PlatformLeadsCard, Strip, Th, VisitsCell,
  btn, btnTone, td, tdBig, tdOurs, tr, trClick,
} from '../components/PlatformBits';

/** One ChatGPT campaign: what OpenAI delivered next to what the CRM holds for
 *  the people it sent. The site files a click under `utm:openai/<medium>/
 *  <utm_campaign>`, and the ad group's query string fills utm_campaign with
 *  {campaign_id} — that is the join. A link without it lands with no campaign;
 *  while exactly one campaign is running it belongs to that one, otherwise it
 *  gets a row of its own that says so. */
interface CampaignRow {
  key: string;
  campaign: ChatGPTCampaign | null;   // null = ChatGPT arrivals that match no campaign
  tags: string[];
  landed: number;
  tapped: number;
  new_leads: number;
  stages: Record<string, number>;
  visits_scheduled: number;
  visits_conducted: number;
}

const UNMATCHED = 'unmatched';
const ADS_MANAGER = 'https://ads.openai.com';
const isChatGPT = (s: SourceFunnel) => ['openai', 'chatgpt'].includes((s.source ?? '').toLowerCase());

const rollUp = (campaigns: ChatGPTCampaign[], sources: SourceFunnel[]): CampaignRow[] => {
  const blank = (key: string, campaign: ChatGPTCampaign | null): CampaignRow =>
    ({ key, campaign, tags: [], landed: 0, tapped: 0, new_leads: 0, stages: {}, visits_scheduled: 0, visits_conducted: 0 });
  const rows = new Map<string, CampaignRow>(campaigns.map((c) => [c.campaign_id, blank(c.campaign_id, c)]));
  const active = campaigns.filter((c) => c.status === 'active');
  sources.filter((s) => s.tag && isChatGPT(s)).forEach((s) => {
    const byId = s.campaign ? campaigns.find((c) => c.campaign_id === s.campaign) : undefined;
    const home = byId ?? (!s.campaign && active.length === 1 ? active[0] : undefined);
    const key = home?.campaign_id ?? UNMATCHED;
    if (!rows.has(key)) rows.set(key, blank(key, null));
    const r = rows.get(key)!;
    r.tags.push(s.tag!);
    r.landed += s.opened;
    r.tapped += s.cta_clicked;
    r.new_leads += s.new_leads;
    r.visits_scheduled += s.visits_scheduled;
    r.visits_conducted += s.visits_conducted;
    Object.entries(s.stages).forEach(([st, n]) => { r.stages[st] = (r.stages[st] ?? 0) + n; });
  });
  return Array.from(rows.values()).sort((a, b) => (b.campaign?.spend ?? -1) - (a.campaign?.spend ?? -1));
};

const n = (v: number) => v.toLocaleString('en-IN');

const ChatGPTPage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [report, setReport] = useState<ChatGPTReport | null>(null);
  const [data, setData] = useState<BySource | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setReport(null); setData(null); setLeads(null); setError(null);
    Promise.all([
      openAIAdsApi.report(range.from, range.to),
      engagementApi.bySource(range.from, range.to),
    ]).then(([rep, bs]) => {
      setReport(rep); setData(bs);
      const tags = rollUp(rep.campaigns, bs.sources).flatMap((r) => r.tags);
      return leadApi.byFirstTouch(tags, range.from, range.to).then(setLeads);
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    openAIAdsApi.refresh()
      .then(load)
      .catch((err: any) => setError(`Refresh failed: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setRefreshing(false));
  };

  if (error) return <Failed what="ChatGPT" error={error} />;

  const rows = report && data ? rollUp(report.campaigns, data.sources) : [];
  if (selected && !rows.some((r) => r.key === selected)) setSelected(null);
  const shown = selected ? rows.filter((r) => r.key === selected) : rows;
  const selectedRow = selected ? shown[0] : undefined;
  const tot = shown.reduce((a, r) => ({
    spend: a.spend + (r.campaign?.spend ?? 0), clicks: a.clicks + (r.campaign?.clicks ?? 0),
    landed: a.landed + r.landed, tapped: a.tapped + r.tapped, leads: a.leads + r.new_leads,
    sched: a.sched + r.visits_scheduled, done: a.done + r.visits_conducted,
  }), { spend: 0, clicks: 0, landed: 0, tapped: 0, leads: 0, sched: 0, done: 0 });

  const strip: StripItem[] = [
    { v: tot.done, k: 'site visits', cls: 'visited' },
    { v: tot.sched, k: 'scheduled', cls: 'gold' },
    { v: tot.leads, k: 'new leads' },
    { v: n(tot.landed), k: 'landed on the site' },
    { v: n(tot.clicks), k: 'clicks ChatGPT counted' },
    { v: fmtINR(tot.spend), k: 'spend' },
    ...(tot.leads > 0 ? [{ v: fmtINR(tot.spend / tot.leads), k: 'per lead' }] : []),
  ];

  const selectedTags = new Set(shown.flatMap((r) => r.tags));
  const leadRows = (leads ?? []).filter((l) => !selected || selectedTags.has(l.acquisition?.src ?? ''));
  const tagToRow = new Map(rows.flatMap((r) => r.tags.map((t) => [t, r] as const)));
  const rowName = (r: CampaignRow) => r.campaign?.name ?? 'Arrived with no campaign on the link';
  const ads = (report?.ads ?? []).filter((a) => !selected || a.campaign_id === selected);
  const untaggedLinks = (report?.ads ?? []).some((a) => a.status === 'active' && !(a.query_string ?? '').includes('{campaign_id}'));

  return (
    <PageFrame
      title="ChatGPT"
      badge={{ platform: 'chatgpt' }}
      subline={<>
        Bhuvana Fortune Greens · ₹ · {report?.synced_at ? <>synced <b className="text-charcoal">{fmtDayTime(report.synced_at)}</b></> : 'sync time unknown'}
        {' · '}<Out href={ADS_MANAGER}>Open Ads Manager</Out>
      </>}
      action={<button onClick={refresh} disabled={refreshing || !report} className={`${btn} ${btnTone.forest}`}>{refreshing ? 'Refreshing…' : 'Refresh from OpenAI'}</button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!(report && data) && <Loading what="ChatGPT" />}

      {report && data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <Section title="Campaigns" note={selectedRow
            ? <>leads below are from <b className="text-charcoal">{rowName(selectedRow)}</b> · <button className="font-semibold text-forest hover:underline" onClick={() => setSelected(null)}>show all</button></>
            : 'click a campaign to see its leads'}>
            <table className="w-full min-w-[1000px] border-collapse">
              <thead><tr>
                <Th left>Campaign</Th><Th>Spend</Th><Th>Clicks</Th><Th>ChatGPT conv.</Th>
                <Th ours>Landed</Th><Th ours>Tapped</Th><Th ours>New leads</Th><Th ours>Stage</Th><Th ours>Site visits</Th><Th>₹ / lead</Th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={10}>No ChatGPT spend and no ChatGPT arrivals in this window.</Empty>}
                {rows.map((r) => {
                  const c = r.campaign;
                  const name = rowName(r);
                  return (
                    <tr key={r.key} onClick={() => setSelected(selected === r.key ? null : r.key)}
                        className={trClick(selected === r.key)}>
                      <td className={`px-3 py-3 ${picked(selected === r.key)}`}>
                        <div className={`font-semibold text-[13.5px] leading-snug truncate max-w-[320px] ${c ? '' : 'text-brick'}`} title={name}>{name}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[11px] text-graphite">
                          {c && <Pill tone={c.status === 'active' ? 'ok' : undefined}>{c.status ?? ''}</Pill>}
                          {c && <span>{[c.objective && `bids for ${c.objective}`, c.daily_budget != null ? `${fmtINR(c.daily_budget)} / day` : null].filter(Boolean).join(' · ')}</span>}
                        </div>
                      </td>
                      <td className={tdBig}>{c ? <>{fmtINR(c.spend)}<div className="text-[11px] text-graphite font-normal">{n(c.impressions)} impressions</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c?.clicks ? <>{n(c.clicks)}<div className="text-[11px] text-graphite">{c.spend ? `${fmtINR(c.spend / c.clicks)} each` : `${((c.clicks / c.impressions) * 100).toFixed(1)}% of views`}</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c?.conversions || <Dash />}</td>
                      <td className={`${td} text-right`}>{r.landed ? n(r.landed) : <Dash />}</td>
                      <td className={`${td} text-right`}>{r.tapped || <Dash />}</td>
                      <td className={tdOurs}>{r.new_leads || <Dash />}</td>
                      <td className="px-3 py-3 text-right">{r.new_leads ? <StageChips stages={r.stages} /> : <Dash />}</td>
                      <VisitsCell done={r.visits_conducted} sched={r.visits_scheduled} />
                      <td className={`${td} text-right`}>{c && r.new_leads ? fmtINR(c.spend / r.new_leads) : <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Note>
              <b className="text-charcoal">Landed</b> is our count of site arrivals tagged <code>utm_source=openai</code>; <b className="text-charcoal">ChatGPT conv.</b> is OpenAI's own count, which stays at zero until our site or server sends it events.
              {untaggedLinks && <> Some live ads link without <code>utm_campaign={'{campaign_id}'}</code>, so their arrivals can't be tied to a campaign — set the ad group's landing-page query string in Ads Manager.</>}
            </Note>
          </Section>

          <Section title="Ads" note={selectedRow ? <>in <b className="text-charcoal">{rowName(selectedRow)}</b></> : 'every live ad, and any that spent in the window'}>
            <table className="w-full min-w-[900px] border-collapse">
              <thead><tr><Th left>Ad</Th><Th>Spend</Th><Th>Impressions</Th><Th>Clicks</Th><Th>ChatGPT conv.</Th></tr></thead>
              <tbody>
                {ads.length === 0 && <Empty cols={5}>No ad in this window.</Empty>}
                {ads.map((a) => {
                  const name = a.title ?? a.name ?? a.ad_id;
                  const rejected = a.review_status === 'rejected';
                  return (
                    <tr key={a.ad_id} className={tr}>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-[13.5px] leading-snug truncate max-w-[420px]" title={name}>{name}</div>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-graphite">
                          <Pill tone={rejected ? 'warn' : a.review_status === 'approved' ? 'ok' : 'gold'}>{rejected ? 'rejected' : a.review_status ?? a.status ?? ''}</Pill>
                          <span className="truncate max-w-[340px]" title={rejected ? a.review_reason ?? '' : a.body ?? ''}>{rejected ? a.review_reason : a.body}</span>
                        </div>
                      </td>
                      <td className={tdBig}>{fmtINR(a.spend)}</td>
                      <td className={`${td} text-right`}>{a.impressions ? n(a.impressions) : <Dash />}</td>
                      <td className={`${td} text-right`}>{a.clicks ? n(a.clicks) : <Dash />}</td>
                      <td className={`${td} text-right`}>{a.conversions || <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>

          <PlatformLeadsCard
            title={selectedRow ? `Leads from ${rowName(selectedRow)}` : 'Leads from ChatGPT'}
            leads={leads}
            rows={leadRows}
            empty="No lead in this window carries a ChatGPT first touch."
            origin={selected ? undefined : { header: 'Campaign', of: (l) => { const r = tagToRow.get(l.acquisition?.src ?? ''); const name = r ? rowName(r) : 'Arrived with no campaign on the link'; return <span title={name}>{name}</span>; } }}
          />
        </>
      )}
    </PageFrame>
  );
};

export default ChatGPTPage;
